import { useState, useCallback, useEffect, useRef } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { getOCELMetadata, getResourceLastActiveDate } from "../../../../services/api/ocel.api";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { useGlobalFilters } from "../../../../stores/GlobalFilterContext/GlobalFilterContext";
import { getBerlinToday, addDaysToDateOnly, formatDateOnly } from "../../../../utils/berlinDate";
import { LAST_ACTIVE_DAY_PRESET } from "../../../../features/filter/TabFilterModal/RollingDayPresets";

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

// Rolling-preset offset (0/-1/-7/-14/-30, see RollingDayPresets) is resolved
// fresh against "today" every time it's needed, instead of trusting whatever
// concrete date was last computed/stored — otherwise "Gestern" silently
// freezes at the date it was clicked instead of tracking the actual
// yesterday on the next mount/refresh. null/undefined offset = manual date,
// returned unchanged. 0 ("Heute") is a valid, falsy offset — never `!offset`.
// LAST_ACTIVE_DAY_PRESET is a third, non-numeric case: it resolves against
// resourceLastActiveDate (the last day THIS tile's configured resources had
// any event — see the reactive fetch below) instead of calendar-today, so it
// rolls forward as new ETL data lands rather than as days pass — falls back
// to fallbackDate until that lookup has resolved. Deliberately NOT the
// dataset's overall last event date (metadata.date_range.end): that can
// belong to a resource never configured on this tile, which would resolve
// to a day with nothing to show.
//
// Deliberately NOT clamped against the dataset's overall last event date for
// the numeric offsets (unlike the manual-date clamp elsewhere in this hook):
// a per-fetch clamp here would silently substitute an older date for offsets
// near "today" (most likely to exceed it under ETL lag) while the displayed
// date stayed unclamped, so the visible date and the actually-fetched date
// would disagree — the tile just shows its normal "No Data" state instead
// when there's nothing for the resolved day, same as AggregationKPI's period
// presets.
const resolveRollingDate = (offset, fallbackDate, resourceLastActiveDate) => {
  if (offset === LAST_ACTIVE_DAY_PRESET) return resourceLastActiveDate || fallbackDate;
  if (offset === null || offset === undefined) return fallbackDate;
  return formatDateOnly(addDaysToDateOnly(getBerlinToday(), offset));
};

const useResourceStatusMonitor = (activeFile, initialSettings = null) => {
  const { post: apiPost, get: apiGet, getRaw: apiGetRaw, currentFilters: filterState } = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const { filterState: filterStateCtx } = useGlobalFilters();
  const configKey = `resourceStatusMonitorConfig_${activeFile || "default"}`;
  const viewKey = `resourceStatusMonitorView_${activeFile || "default"}`;

  // Data / loading state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [monitorData, setMonitorData] = useState(null);
  // Last day THIS tile's own configuredResources had any event — feeds the
  // "Letzter aktiver Tag" preset (see resolveRollingDate above). Deliberately
  // resource-scoped, not dataset-wide: the dataset's overall last event date
  // (metadata.date_range.end, used below purely as an upper-bound clamp for
  // manually-picked dates) can belong to a resource never configured on this
  // tile.
  const [resourceLastActiveDate, setResourceLastActiveDate] = useState(null);

  // Metadata state
  const [objectTypes, setObjectTypes] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // TileConfig (persistent, set in Edit Mode)
  const [title, setTitle] = useState("Resource Status Monitor");
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [cycleStartActivity, setCycleStartActivity] = useState("cycle_start");
  const [cycleEndActivity, setCycleEndActivity] = useState("cycle_end");
  const [configuredResources, setConfiguredResources] = useState([]);
  const [shiftStart, setShiftStart] = useState("06:00");
  const [shiftEnd, setShiftEnd] = useState("14:00");
  const [breaks, setBreaks] = useState([]);
  const [toleranceThreshold, setToleranceThreshold] = useState(60);
  const [ignoreShortTransitions, setIgnoreShortTransitions] = useState(false);
  const [includeNoDataResources, setIncludeNoDataResources] = useState(false);
  const [multiResourceMode, setMultiResourceMode] = useState("accumulated");
  const [resourceColumn, setResourceColumn] = useState("ressource");
  const [downtimeRedThreshold, setDowntimeRedThreshold] = useState(5); // minutes
  const [blockSizeMinutes, setBlockSizeMinutes] = useState(15); // one of 5/10/15/20
  const [showShiftOnly, setShowShiftOnly] = useState(false);
  // Click-triggered extended tooltip (see ResourceTracker) — object
  // attribute columns to show for the objects behind a clicked slot,
  // analogous to AggregationKPI/EfficiencyKPI's tooltipAttributes but
  // fetched on click (not bundled into the tile's own mount-time fetch).
  // tooltipObjectType is DELIBERATELY independent of selectedObjectType —
  // the latter plays no role in the resource/downtime computation itself
  // (see get_resource_status_monitor, which only reads it for the cache
  // key), so it may not be the object type actually linked to
  // resource-tagged events; empty means "fall back to selectedObjectType".
  const [tooltipObjectType, setTooltipObjectType] = useState("");
  const [tooltipAttributes, setTooltipAttributes] = useState([]);

  // ViewState (runtime, set in View Mode)
  const [viewResources, setViewResources] = useState([]);
  const [selectedDate, setSelectedDate] = useState(yesterdayISO());
  // Active rolling-preset offset, or null when selectedDate is a manually
  // picked, frozen date (see resolveRollingDate above).
  const [rollingOffset, setRollingOffset] = useState(null);
  // Mirrors rollingOffset for the manual-date clamps below, without
  // adding rollingOffset to their effect/callback dependencies (that would
  // make them re-run on every offset change for no reason — they only need
  // the current value at the moment metadata actually arrives).
  const rollingOffsetRef = useRef(rollingOffset);
  useEffect(() => { rollingOffsetRef.current = rollingOffset; }, [rollingOffset]);

  // Track whether full initialization (restore + metadata) is complete
  const isFullyInitializedRef = useRef(false);

  // Skip the activeFile reset on initial mount so initialSettings values survive
  const isFirstMountRef = useRef(true);

  // Request dedup ref
  const inFlightSignatureRef = useRef(null);
  const lastResolvedSignatureRef = useRef(null);
  const lastResolvedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);

  // Restoration uses STATE (not refs) so that persist effects only see
  // the "restored" flag on the SAME render where restored values are applied.
  // Refs update synchronously — the persist effect in the same effect-cycle
  // would see the flag=true but still read the OLD (default) state values,
  // overwriting localStorage with defaults.
  const [configRestoredKey, setConfigRestoredKey] = useState(null);
  const [viewRestoredKey, setViewRestoredKey] = useState(null);

  // Restore TileConfig from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(configKey);
      if (!raw) {
        setConfigRestoredKey(configKey);
        return;
      }
      const cfg = JSON.parse(raw);

      if (typeof cfg.title === "string") setTitle(cfg.title);
      if (typeof cfg.selectedObjectType === "string")
        setSelectedObjectType(cfg.selectedObjectType);
      if (typeof cfg.cycleStartActivity === "string")
        setCycleStartActivity(cfg.cycleStartActivity);
      if (typeof cfg.cycleEndActivity === "string")
        setCycleEndActivity(cfg.cycleEndActivity);
      if (Array.isArray(cfg.configuredResources))
        setConfiguredResources(cfg.configuredResources);
      if (typeof cfg.shiftStart === "string") setShiftStart(cfg.shiftStart);
      if (typeof cfg.shiftEnd === "string") setShiftEnd(cfg.shiftEnd);
      if (Array.isArray(cfg.breaks)) setBreaks(cfg.breaks);
      if (typeof cfg.toleranceThreshold === "number")
        setToleranceThreshold(cfg.toleranceThreshold);
      if (typeof cfg.ignoreShortTransitions === "boolean")
        setIgnoreShortTransitions(cfg.ignoreShortTransitions);
      if (typeof cfg.includeNoDataResources === "boolean")
        setIncludeNoDataResources(cfg.includeNoDataResources);
      if (typeof cfg.multiResourceMode === "string")
        setMultiResourceMode(cfg.multiResourceMode);
      if (typeof cfg.resourceColumn === "string")
        setResourceColumn(cfg.resourceColumn);
      if (typeof cfg.downtimeRedThreshold === "number")
        setDowntimeRedThreshold(cfg.downtimeRedThreshold);
      if ([5, 10, 15, 20].includes(cfg.blockSizeMinutes))
        setBlockSizeMinutes(cfg.blockSizeMinutes);
      if (typeof cfg.showShiftOnly === "boolean")
        setShowShiftOnly(cfg.showShiftOnly);
      if (typeof cfg.tooltipObjectType === "string")
        setTooltipObjectType(cfg.tooltipObjectType);
      if (Array.isArray(cfg.tooltipAttributes))
        setTooltipAttributes(cfg.tooltipAttributes);
    } catch {
      // ignore
    }
    // Batched with the setters above — persist effect only sees this
    // on the NEXT render, where the restored values are also committed.
    setConfigRestoredKey(configKey);
  }, [configKey]);

  // Restore ViewState from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(viewKey);
      if (!raw) {
        setViewRestoredKey(viewKey);
        return;
      }
      const vs = JSON.parse(raw);

      if (Array.isArray(vs.viewResources)) setViewResources(vs.viewResources);
      if (typeof vs.selectedDate === "string") setSelectedDate(vs.selectedDate);
      if (vs.rollingOffset === null || typeof vs.rollingOffset === "number" || vs.rollingOffset === LAST_ACTIVE_DAY_PRESET) setRollingOffset(vs.rollingOffset);
    } catch {
      // ignore
    }
    setViewRestoredKey(viewKey);
  }, [viewKey]);

  // Restore from centralised config (overrides localStorage on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.selectedObjectType !== undefined) setSelectedObjectType(s.selectedObjectType);
    if (s.cycleStartActivity !== undefined) setCycleStartActivity(s.cycleStartActivity);
    if (s.cycleEndActivity !== undefined) setCycleEndActivity(s.cycleEndActivity);
    if (s.configuredResources !== undefined) setConfiguredResources(s.configuredResources);
    if (s.shiftStart !== undefined) setShiftStart(s.shiftStart);
    if (s.shiftEnd !== undefined) setShiftEnd(s.shiftEnd);
    if (s.breaks !== undefined) setBreaks(s.breaks);
    if (s.toleranceThreshold !== undefined) setToleranceThreshold(s.toleranceThreshold);
    if (s.ignoreShortTransitions !== undefined) setIgnoreShortTransitions(s.ignoreShortTransitions);
    if (s.includeNoDataResources !== undefined) setIncludeNoDataResources(s.includeNoDataResources);
    if (s.multiResourceMode !== undefined) setMultiResourceMode(s.multiResourceMode);
    if (s.downtimeRedThreshold !== undefined) setDowntimeRedThreshold(s.downtimeRedThreshold);
    if ([5, 10, 15, 20].includes(s.blockSizeMinutes)) setBlockSizeMinutes(s.blockSizeMinutes);
    if (s.showShiftOnly !== undefined) setShowShiftOnly(s.showShiftOnly);
    if (s.tooltipObjectType !== undefined) setTooltipObjectType(s.tooltipObjectType);
    if (s.tooltipAttributes !== undefined) setTooltipAttributes(s.tooltipAttributes);
    if (s.viewResources !== undefined) setViewResources(s.viewResources);
    if (s.selectedDate !== undefined) setSelectedDate(s.selectedDate);
    if (s.rollingOffset !== undefined) setRollingOffset(s.rollingOffset);
  }, []);

  // Persist TileConfig — only when the restore for THIS key has been rendered
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (configRestoredKey !== configKey) return;
    try {
      window.localStorage.setItem(
        configKey,
        JSON.stringify({
          title,
          selectedObjectType,
          cycleStartActivity,
          cycleEndActivity,
          configuredResources,
          shiftStart,
          shiftEnd,
          breaks,
          toleranceThreshold,
          ignoreShortTransitions,
          includeNoDataResources,
          multiResourceMode,
          resourceColumn,
          downtimeRedThreshold,
          blockSizeMinutes,
          showShiftOnly,
          tooltipObjectType,
          tooltipAttributes,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    configKey,
    configRestoredKey,
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNoDataResources,
    multiResourceMode,
    resourceColumn,
    downtimeRedThreshold,
    blockSizeMinutes,
    showShiftOnly,
    tooltipObjectType,
    tooltipAttributes,
  ]);

  // Persist ViewState — only when the restore for THIS key has been rendered
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (viewRestoredKey !== viewKey) return;
    try {
      window.localStorage.setItem(
        viewKey,
        JSON.stringify({
          viewResources,
          selectedDate,
          rollingOffset,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [viewKey, viewRestoredKey, viewResources, selectedDate, rollingOffset]);

  // Keep the displayed selectedDate in sync with an active rolling preset —
  // e.g. on mount/reload with a restored rollingOffset. fetchMonitorData
  // resolves fresh on its own regardless; this only keeps the toolbar
  // DateInput and "Currently selected" text (which read selectedDate
  // directly) from showing a stale date.
  useEffect(() => {
    if (rollingOffset === null || rollingOffset === undefined) return;
    setSelectedDate((prev) => {
      const resolved = resolveRollingDate(rollingOffset, prev, resourceLastActiveDate);
      return resolved === prev ? prev : resolved;
    });
  }, [rollingOffset, resourceLastActiveDate]);

  // Break helpers
  const addBreak = useCallback(() => {
    setBreaks((prev) => [...prev, { start: "12:00", end: "12:30" }]);
  }, []);

  const removeBreak = useCallback((idx) => {
    setBreaks((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateBreak = useCallback((idx, field, value) => {
    setBreaks((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
    );
  }, []);

  // Reactive metadata sync from OcelContext (zero API calls when no filters)
  useEffect(() => {
    if (!metadata?.object_types) return;
    const hasActiveFilters = Object.values(filterStateCtx).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );
    if (hasActiveFilters) return;

    const types = Object.keys(metadata.object_types);
    setObjectTypes(types);

    if (metadata.resource_values) {
      setAvailableResources(metadata.resource_values);
    }
    if (metadata.resource_column) {
      setResourceColumn(metadata.resource_column);
    }

    if (metadata.date_range?.end) {
      const lastDate = metadata.date_range.end.slice(0, 10);
      // Only clamp in manual mode — with a rolling offset active, the sync
      // effect above is the sole source of truth for selectedDate; clamping
      // here too would silently override its freshly resolved date whenever
      // metadata arrives (e.g. right after mount), reintroducing the same
      // display/fetch mismatch that resolveRollingDate already avoids.
      if (rollingOffsetRef.current === null || rollingOffsetRef.current === undefined) {
        setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
      }
    }

    setIsMetadataLoaded(true);
  }, [metadata, filterStateCtx, activeFile]);

  // Resource-scoped "last active day" — refetched whenever the tile's own
  // committed resources (or the resolved resource column) change, so
  // LAST_ACTIVE_DAY_PRESET always reflects the current configuration. Unlike
  // the metadata sync above, this always needs a network call (not derivable
  // from OcelContext) since it depends on this tile's specific resources.
  useEffect(() => {
    if (!activeFile || configuredResources.length === 0) {
      setResourceLastActiveDate(null);
      return;
    }
    let cancelled = false;
    getResourceLastActiveDate(activeFile, configuredResources, resourceColumn)
      .then((res) => {
        if (!cancelled) setResourceLastActiveDate(res?.last_active_date ?? null);
      })
      .catch(() => {
        if (!cancelled) setResourceLastActiveDate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeFile, configuredResources, resourceColumn]);

  // Mark fully initialized once both localStorage restores and metadata are ready
  useEffect(() => {
    if (
      configRestoredKey === configKey &&
      viewRestoredKey === viewKey &&
      isMetadataLoaded
    ) {
      isFullyInitializedRef.current = true;
    } else {
      isFullyInitializedRef.current = false;
    }
  }, [configRestoredKey, configKey, viewRestoredKey, viewKey, isMetadataLoaded]);

  // Auto-select first object type
  useEffect(() => {
    if (!selectedObjectType && objectTypes.length > 0) {
      setSelectedObjectType(objectTypes[0]);
    }
  }, [objectTypes, selectedObjectType]);

  // Fetch metadata
  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    const hasActiveFilters = Object.values(filterStateCtx).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );

    if (!hasActiveFilters) {
      setIsMetadataLoaded(true);
      return true;
    }

    setLoading(true);
    setError(null);

    const tryPostEndpoints = async (endpoints, payload, label) => {
      const tried = [];
      for (const ep of endpoints) {
        try {
          const res = await apiPost(ep, payload, {}, label);
          return { res, endpoint: ep };
        } catch (e) {
          tried.push({ endpoint: ep, message: e?.message || String(e) });
          if (e?.message && /not found|404/i.test(e.message)) continue;
          throw e;
        }
      }
      const details = tried
        .map((t) => `${t.endpoint} -> ${t.message}`)
        .join(" | ");
      throw new Error(`All ${label} endpoints failed: ${details}`);
    };

    const tryGetEndpoints = async (endpoints, params, label) => {
      const tried = [];
      for (const ep of endpoints) {
        try {
          const res = await apiGet(ep, params, {}, label);
          return { res, endpoint: ep };
        } catch (e) {
          tried.push({ endpoint: ep, message: e?.message || String(e) });
          if (e?.message && /not found|404/i.test(e.message)) continue;
          throw e;
        }
      }
      const details = tried
        .map((t) => `${t.endpoint} -> ${t.message}`)
        .join(" | ");
      throw new Error(`All ${label} endpoints failed: ${details}`);
    };

    try {
      const basicRequest = { file_type: activeFile };

      const { res: objectTypesResponse } = await tryPostEndpoints(
        ["/filtered-object-types", "/ocel/filtered-object-types"],
        basicRequest,
        "Object Types",
      );
      const types = objectTypesResponse.objectTypes || [];
      setObjectTypes(types);
      if (!selectedObjectType && types.length > 0) {
        setSelectedObjectType(types[0]);
      }

      const { res: resourceResponse } = await tryGetEndpoints(
        ["/ocel/resource-values", "/resource-values"],
        { file_type: activeFile },
        "Resource Values",
      );

      const resources = resourceResponse.values || [];
      setAvailableResources(resources);
      setConfiguredResources((prev) => {
        const nextConfigured = prev.filter((r) => resources.includes(r));
        setViewResources((existingView) => {
          const filteredView = existingView.filter((r) =>
            resources.includes(r),
          );
          if (filteredView.length > 0) return filteredView;
          return nextConfigured;
        });
        return nextConfigured;
      });

      if (resourceResponse.used_column) {
        setResourceColumn(resourceResponse.used_column.replace(/^ocel:/, ""));
      }

      try {
        const metaRes = await getOCELMetadata(activeFile);
        if (metaRes?.date_range?.end) {
          const lastDate = metaRes.date_range.end.slice(0, 10);
          // See the reactive metadata-sync effect above for why this is
          // skipped while a rolling offset is active.
          if (rollingOffsetRef.current === null || rollingOffsetRef.current === undefined) {
            setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
          }
        }
      } catch {
        // Non-critical
      }

      setIsMetadataLoaded(true);
      return true;
    } catch (err) {
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, apiPost, apiGet, apiGetRaw, filterStateCtx, selectedObjectType]);

  // Fetch monitor data
  const fetchMonitorData = useCallback(
    async (overrides = {}) => {
      const effectiveConfiguredResources =
        overrides.configuredResources ?? configuredResources;
      const effectiveViewResources = overrides.viewResources ?? viewResources;
      const effectiveResources =
        effectiveViewResources.length > 0
          ? effectiveViewResources
          : effectiveConfiguredResources;
      const effectiveSelectedObjectType =
        overrides.selectedObjectType ?? selectedObjectType;
      const effectiveShiftStart = overrides.shiftStart ?? shiftStart;
      const effectiveShiftEnd = overrides.shiftEnd ?? shiftEnd;
      const effectiveBreaks = overrides.breaks ?? breaks;
      const effectiveToleranceThreshold =
        overrides.toleranceThreshold ?? toleranceThreshold;
      const effectiveIgnoreShortTransitions =
        overrides.ignoreShortTransitions ?? ignoreShortTransitions;
      const effectiveIncludeNoDataResources =
        overrides.includeNoDataResources ?? includeNoDataResources;
      const effectiveMultiResourceMode =
        overrides.multiResourceMode ?? multiResourceMode;
      const rawBlockSizeMinutes = overrides.blockSizeMinutes ?? blockSizeMinutes;
      const effectiveBlockSizeMinutes = [5, 10, 15, 20].includes(rawBlockSizeMinutes)
        ? rawBlockSizeMinutes
        : 15;
      const effectiveRollingOffset =
        overrides.rollingOffset !== undefined ? overrides.rollingOffset : rollingOffset;
      const effectiveSelectedDate = resolveRollingDate(
        effectiveRollingOffset,
        overrides.selectedDate ?? selectedDate,
        resourceLastActiveDate,
      );

      if (!activeFile) {
        setError("No active file selected");
        return false;
      }
      if (!effectiveSelectedObjectType) {
        setError("Please select an object type");
        return false;
      }
      if (effectiveResources.length === 0) {
        setError("Please select at least one resource");
        return false;
      }
      if (!effectiveSelectedDate) {
        setError("Please select a date");
        return false;
      }

      const toTimestamp = (hhmm) => `${hhmm}:00`;

      const payload = {
        file_type: activeFile,
        resources: effectiveResources,
        shift: {
          start_time: toTimestamp(effectiveShiftStart),
          end_time: toTimestamp(effectiveShiftEnd),
        },
        breaks: effectiveBreaks.map((b) => ({
          start_time: toTimestamp(b.start),
          end_time: toTimestamp(b.end),
        })),
        tolerance_threshold_seconds: effectiveToleranceThreshold,
        ignore_short_transitions: effectiveIgnoreShortTransitions,
        include_no_data_resources: effectiveIncludeNoDataResources,
        multi_resource_mode: effectiveMultiResourceMode,
        cycle_start_column: cycleStartActivity,
        cycle_end_column: cycleEndActivity,
        production_order_column: effectiveSelectedObjectType,
        resource_column: resourceColumn,
        selected_date: effectiveSelectedDate,
        block_size_minutes: effectiveBlockSizeMinutes,
      };

      const requestSignature = JSON.stringify({
        ...payload,
        resources: [...payload.resources].sort(),
      });

      if (inFlightSignatureRef.current === requestSignature) return true;
      const now = Date.now();
      if (
        lastResolvedSignatureRef.current === requestSignature &&
        now - lastResolvedAtRef.current < 1000
      ) {
        return true;
      }

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;

      try {
        inFlightSignatureRef.current = requestSignature;
        const response = await apiPost(
          "/kpi/resource-status-monitor",
          payload,
          {},
          "Resource Status Monitor",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setMonitorData(response);
        lastResolvedSignatureRef.current = requestSignature;
        lastResolvedAtRef.current = Date.now();
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setMonitorData(null);
        let msg = err?.message || "Failed to load monitor data";
        if (msg.includes("404")) {
          msg = "Endpoint not found. Please check if the backend is running.";
        } else if (msg.includes("400")) {
          msg = "Invalid configuration. Please check your settings.";
        }
        setError(msg);
        return false;
      } finally {
        if (inFlightSignatureRef.current === requestSignature) {
          inFlightSignatureRef.current = null;
        }
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      activeFile,
      apiPost,
      breaks,
      configuredResources,
      cycleEndActivity,
      cycleStartActivity,
      resourceLastActiveDate,
      includeNoDataResources,
      ignoreShortTransitions,
      multiResourceMode,
      resourceColumn,
      selectedDate,
      rollingOffset,
      selectedObjectType,
      shiftEnd,
      shiftStart,
      toleranceThreshold,
      viewResources,
    ],
  );

  // Clear localStorage on unmount so re-adding the component starts
  // with the "Configure" prompt instead of restoring stale config.
  const configKeyRef = useRef(configKey);
  const viewKeyRef = useRef(viewKey);
  useEffect(() => { configKeyRef.current = configKey; }, [configKey]);
  useEffect(() => { viewKeyRef.current = viewKey; }, [viewKey]);
  useEffect(() => {
    return () => {
      try {
        window.localStorage.removeItem(configKeyRef.current);
        window.localStorage.removeItem(viewKeyRef.current);
      } catch { /* no-op */ }
    };
  }, []);

  // Reset on file change — clear user selections and chart output
  // so the "Configure" prompt appears for the new file.
  // The restore effect (dep: [configKey]) will then run and restore
  // any previously saved config for the new file from localStorage.
  // Skip on initial mount so initialSettings values (from dashboardConfig) survive.
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    setConfiguredResources([]);
    setViewResources([]);
    setSelectedObjectType("");
    setMonitorData(null);
    setError(null);
  }, [activeFile]);

  // Keep viewResources in sync with configuredResources.
  // Only applies the "fall back to all configuredResources" when the view
  // state has been restored — otherwise the fallback overwrites the saved
  // selection with ALL configured resources before the restore takes effect.
  useEffect(() => {
    if (viewRestoredKey !== viewKey) return; // still restoring — skip
    setViewResources((prev) => {
      const allowed = prev.filter((r) => configuredResources.includes(r));
      if (allowed.length > 0) return allowed;
      return configuredResources;
    });
  }, [configuredResources, viewRestoredKey, viewKey]);

  return {
    // Data
    loading,
    error,
    monitorData,
    resourceLastActiveDate,
    // Metadata
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // Initialization
    isFullyInitializedRef,
    // TileConfig
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNoDataResources,
    multiResourceMode,
    resourceColumn,
    downtimeRedThreshold,
    blockSizeMinutes,
    showShiftOnly,
    tooltipObjectType,
    tooltipAttributes,
    // ViewState
    viewResources,
    selectedDate,
    rollingOffset,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setConfiguredResources,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setDowntimeRedThreshold,
    setBlockSizeMinutes,
    setShowShiftOnly,
    setTooltipObjectType,
    setTooltipAttributes,
    // ViewState setters
    setViewResources,
    setSelectedDate,
    setRollingOffset,
    // Break helpers
    addBreak,
    removeBreak,
    updateBreak,
    // Actions
    fetchMetadata,
    fetchMonitorData,
  };
};

export { useResourceStatusMonitor };
